import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as actionCreator from "../../actions";

import {
    CactusLabelInput,
    DashboardTitle,
    DashboardWrapper
} from '../../components';

import useFormValidatorText from '../../hooks/validators/use-form-validator-text';
import UserService from '../../services/use_case/user/UserService';
import { getFullName } from '../../models/User';
import useTheme, { isThemeSwitcherEnabled } from '../../hooks/useTheme';

export type UserInputs = {
    name: string;
    last_name: string;
    email: string;
    password: string;
}

interface ProfileProps {};

const Profile: React.FC<ProfileProps> = () => {

    const [t, i18n] = useTranslation();
    const dispatch = useDispatch();

    const { user } = useSelector( (store: any) => store.authReducer )

    const { handleSubmit, register, errors } = useForm<UserInputs>({
        defaultValues: {
            ...user
        }
    });

    const [isLoading, setisLoading] = useState(false)
    const { theme, setTheme } = useTheme();

    const textValidator = register(useFormValidatorText());

    const _handleOnSubmit = async(data: UserInputs) => {
        setisLoading(true);
        try {
            await new UserService().update({
                ...user,
                ...data,
            });
            dispatch( actionCreator.authMe() );
            setisLoading(false);
        } catch (err){
            setisLoading(false);
        }
    }

    const _getInitials = (): string => {
        const first = user?.name?.charAt(0) ?? '';
        const last = user?.last_name?.charAt(0) ?? '';
        return `${first}${last}`.toUpperCase() || '?';
    }

    const _getRole = (): string => {
        if (!user?.rol || user.rol.length === 0) return '';
        return t(`rol_${user.rol[0]}`);
    }

    const _getMemberSince = (): string => {
        if (!user?.created_at) return '';
        return new Date(user.created_at).toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: 'long',
        });
    }

    return(
        <DashboardWrapper>
            <DashboardTitle title={t('Profile')} isLoading={isLoading}/>

            <div className="row">

                {/* Profile info card */}
                <div className="col-12 col-md-6 col-lg-3 mb-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="profile__header">
                                <div className="profile__avatar">
                                    {_getInitials()}
                                </div>
                                <div className="profile__name">{getFullName(user).trim() || user?.email}</div>
                                <div className="profile__email">{user?.email}</div>
                                {user?.rol?.length > 0 && (
                                    <div className="profile__role">{_getRole()}</div>
                                )}
                            </div>
                            {user?.created_at && (
                                <div className="profile__meta">
                                    <div className="profile__meta-item">
                                        <span className="profile__meta-label">{t('profile_member_since')}</span>
                                        <span className="profile__meta-value">{_getMemberSince()}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Appearance card */}
                {isThemeSwitcherEnabled() && (
                <div className="col-12 col-md-6 col-lg-3 mb-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <p className="profile__section-title">{t('profile_appearance')}</p>
                            <div className="theme-switcher">
                                <button
                                    type="button"
                                    className={`theme-switcher__option${theme === 'cactusds' ? ' theme-switcher__option--active' : ''}`}
                                    onClick={() => setTheme('cactusds')}
                                    aria-pressed={theme === 'cactusds'}
                                >
                                    <span className="theme-switcher__swatch theme-switcher__swatch--dark">
                                        <span /><span /><span />
                                    </span>
                                    <span className="theme-switcher__label">
                                        <strong>{t('theme_cactusds')}</strong>
                                        <small>{t('theme_cactusds_desc')}</small>
                                    </span>
                                    {theme === 'cactusds' && (
                                        <span className="theme-switcher__check">
                                            <i className="iconsminds-yes" />
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={`theme-switcher__option${theme === 'alternative' ? ' theme-switcher__option--active' : ''}`}
                                    onClick={() => setTheme('alternative')}
                                    aria-pressed={theme === 'alternative'}
                                >
                                    <span className="theme-switcher__swatch theme-switcher__swatch--alternative">
                                        <span /><span /><span />
                                    </span>
                                    <span className="theme-switcher__label">
                                        <strong>{t('theme_alternative')}</strong>
                                        <small>{t('theme_alternative_desc')}</small>
                                    </span>
                                    {theme === 'alternative' && (
                                        <span className="theme-switcher__check">
                                            <i className="iconsminds-yes" />
                                        </span>
                                    )}
                                </button>
                                {/* MARK: CAMELIA THEME DISABLED FOR NOW */}
                                {/* <button
                                    type="button"
                                    className={`theme-switcher__option${theme === 'camelia' ? ' theme-switcher__option--active' : ''}`}
                                    onClick={() => setTheme('camelia')}
                                    aria-pressed={theme === 'camelia'}
                                >
                                    <span className="theme-switcher__swatch theme-switcher__swatch--camelia">
                                        <span /><span /><span />
                                    </span>
                                    <span className="theme-switcher__label">
                                        <strong>{t('theme_camelia')}</strong>
                                        <small>{t('theme_camelia_desc')}</small>
                                    </span>
                                    {theme === 'camelia' && (
                                        <span className="theme-switcher__check">
                                            <i className="iconsminds-yes" />
                                        </span>
                                    )}
                                </button> */}
                            </div>
                        </div>
                    </div>
                </div>
                )}

                {/* Edit form card */}
                <div className="col-12 col-md-12 col-lg-6 mb-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <form onSubmit={handleSubmit(_handleOnSubmit)}>

                                <p className="profile__section-title">{t('profile_personal_info')}</p>
                                <div className="row">
                                    <div className="col-12 col-lg-6">
                                        <CactusLabelInput
                                            labelTitle={t('Name')}
                                            inputAttributes={{
                                                id: "name",
                                                name: "name",
                                                type: "text",
                                                placeholder: t('Name'),
                                                autoComplete: "given-name",
                                                ref: textValidator,
                                            }}
                                            errorMessage={errors.name ? errors.name.message : null}
                                        />
                                    </div>
                                    <div className="col-12 col-lg-6 mt-3 mt-lg-0">
                                        <CactusLabelInput
                                            labelTitle={t('Last Name')}
                                            inputAttributes={{
                                                id: "last_name",
                                                name: "last_name",
                                                type: "text",
                                                placeholder: t('Last Name'),
                                                autoComplete: "family-name",
                                                ref: register,
                                            }}
                                            errorMessage={errors.last_name ? errors.last_name.message : null}
                                        />
                                    </div>
                                    <div className="col-12 mt-3">
                                        <CactusLabelInput
                                            labelTitle={t('Email')}
                                            inputAttributes={{
                                                name: "email",
                                                value: user?.email,
                                                disabled: true,
                                                classprops: 'disabled',
                                                ref: register,
                                            }}
                                        />
                                    </div>
                                </div>

                                <hr className="profile__divider" />

                                <p className="profile__section-title">{t('profile_security')}</p>
                                <div className="row">
                                    <div className="col-12 col-lg-6">
                                        <CactusLabelInput
                                            labelTitle={t('Change password')}
                                            inputAttributes={{
                                                id: "password",
                                                name: "password",
                                                type: "password",
                                                placeholder: "••••••••",
                                                autoComplete: "new-password",
                                                ref: register,
                                            }}
                                            errorMessage={errors.password ? errors.password.message : null}
                                        />
                                    </div>
                                </div>

                                <div className="text-right mt-4">
                                    <button
                                        type="submit"
                                        className="btn btn-cactus"
                                        disabled={isLoading}
                                    >
                                        {t('Update_profile')}
                                    </button>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>

            </div>
        </DashboardWrapper>
    );

};

export default React.memo(Profile);
